import React from "react";

import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import { FullScreenContainer } from "components/FullScreenContainer";

import { Settings } from "constants/settings";

import {
  getLastSeenSaleLotsByDraftingId,
  getSetting,
  selectWeighedSaleLotsInWeighedOrder,
  WatcherSaleLot,
} from "selectors";

import DraftingInformationSwitcher from "./DraftingInformationSelector";
import {
  AverageWeight,
  BoundingBox,
  CentsPerKilo,
  DollarsPerHead,
  TotalWeight,
} from "./WatcherComponents";

interface OnScalesViewProps {
  detailed?: boolean;
}

const OnScalesBoundingBox = styled(BoundingBox)`
  display: flex;
  flex-direction: column;
`;

const HighContrastTable = styled.table`
  ${({ theme }) => `
  border-collapse: collapse;
  font-size: ${theme.fontSizes.theta}px;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.grayF3};

  width: 100%;
  flex: 1;
  min-height: 0;

  td {
    width: 50%;
    padding: 0 ${theme.space[2]}px;
    font-weight: bold;
  }

  `}
`;

const OnScalesTableRows = ({
  detailed,
  onScalesLot,
}: {
  detailed: boolean;
  onScalesLot: Partial<WatcherSaleLot>;
}) => (
  <tbody>
    <tr>
      <td>PEN NUMBER</td>
      <td className="current">{onScalesLot.penName || "-"}</td>
    </tr>

    <tr>
      <td>PAINT MARK</td>
      <td className="current">
        {onScalesLot.marks?.map(mark => mark.location).join("/") || "-"}
      </td>
    </tr>
    {detailed && (
      <>
        <tr>
          <td>HEAD</td>
          <td className="current">
            {onScalesLot.quantity ? `${onScalesLot.quantity}` : "-"}
          </td>
        </tr>
      </>
    )}
    <tr>
      <td>CENTS/Kg</td>
      <td className="current">
        <CentsPerKilo saleLot={onScalesLot} noSymbol />
      </td>
    </tr>
    <tr>
      <td>AVERAGE Kg</td>
      <td className="current">
        <AverageWeight saleLot={onScalesLot} includeUnits={false} />
      </td>
    </tr>
    {detailed && (
      <>
        <tr>
          <td>TOTAL Kg</td>
          <td className="current">
            <TotalWeight saleLot={onScalesLot} includeUnits={false} />
          </td>
        </tr>
      </>
    )}
    <tr>
      <td>AVERAGE $</td>
      <td className="current">
        <DollarsPerHead saleLot={onScalesLot} noSymbol />
      </td>
    </tr>
  </tbody>
);

export const OnScalesView = ({ detailed = false }: OnScalesViewProps) => {
  const draftingInformationId = useSelector(
    getSetting(Settings.saleWatcherDraftingInformationId),
  );

  const lastSeenLots: WatcherSaleLot[] = useSelector(
    getLastSeenSaleLotsByDraftingId(draftingInformationId),
  );
  const onScalesLot: Partial<WatcherSaleLot> = lastSeenLots?.[0] || {};

  return (
    <FullScreenContainer>
      <OnScalesBoundingBox>
        <DraftingInformationSwitcher />
        <HighContrastTable>
          <OnScalesTableRows detailed={detailed} onScalesLot={onScalesLot} />
        </HighContrastTable>
      </OnScalesBoundingBox>
    </FullScreenContainer>
  );
};

export const OnScalesDetailedView = () => <OnScalesView detailed />;

export const OnScalesDetailedByWeightView = () => {
  const weighedLots: WatcherSaleLot[] = useSelector(
    selectWeighedSaleLotsInWeighedOrder,
  );
  const onScalesLot: Partial<WatcherSaleLot> = weighedLots?.at(-1) || {};

  return (
    <FullScreenContainer>
      <OnScalesBoundingBox>
        <HighContrastTable>
          <OnScalesTableRows detailed onScalesLot={onScalesLot} />
        </HighContrastTable>
      </OnScalesBoundingBox>
    </FullScreenContainer>
  );
};
